import {SelectField, type SelectFieldOption} from '@wandb/components';
import React, {useCallback, useMemo, useState} from 'react';

import {type SettingSelectOptionType} from '../types';

const MAX_OPTIONS_DISPLAYED = 100;

export type LinePlotXAxisProps<OptionType extends SettingSelectOptionType> = {
  id?: string;
  className?: string;
  options: OptionType[];
  value: OptionType | undefined;
  onChange: (selected: OptionType | null) => void;
};

/**
 * This is a Select component used in the settings drawer.
 * It is searchable and will truncate options to a max list of 100
 * to provide better UX experience for workspaces with a large
 * set of metrics.
 */
export const SettingSelect = <OptionType extends SettingSelectOptionType>({
  id,
  className,
  onChange,
  options,
  value,
}: LinePlotXAxisProps<OptionType>) => {
  const [searchText, setSearchText] = useState('');

  const selectOptions: SelectFieldOption[] = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();
    const filteredOptions =
      normalizedSearchText === ''
        ? options
        : options.filter(option =>
            option.label.toLowerCase().includes(normalizedSearchText)
          );
    const visibleOptions = truncateOptions(filteredOptions);
    const optionsWithSelectedValue =
      normalizedSearchText === ''
        ? includeSelectedOption(visibleOptions, value)
        : visibleOptions;

    return optionsWithSelectedValue.map(option => ({
      id: option.value,
      label: option.label,
    }));
  }, [options, searchText, value]);

  const handleChange = useCallback(
    (selectedValue: React.Key | null) => {
      if (selectedValue == null) {
        onChange(null);
        return;
      }
      onChange(
        options.find(option => option.value === String(selectedValue)) ?? null
      );
    },
    [onChange, options]
  );

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setSearchText('');
    }
  }, []);

  const search = useMemo(
    () => ({value: searchText, onInput: setSearchText}),
    [searchText]
  );

  return (
    <div className={className}>
      <SelectField
        id={id}
        aria-label="Setting"
        fullWidth
        items={selectOptions}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        search={search}
        value={value?.value ?? null}
      />
    </div>
  );
};

const truncateOptions = <OptionType extends SettingSelectOptionType>(
  options: OptionType[]
) => {
  return options.slice(0, MAX_OPTIONS_DISPLAYED);
};

const includeSelectedOption = <OptionType extends SettingSelectOptionType>(
  options: OptionType[],
  value: OptionType | undefined
) => {
  if (value == null || options.some(option => option.value === value.value)) {
    return options;
  }
  return [value, ...options.slice(0, MAX_OPTIONS_DISPLAYED - 1)];
};
